'use client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import React, { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import DynamicMaskedInput from '@/components/Form/DynamicMaskedInput'
import { PASSWORD_LINK_EXPIRATION_NOTICE } from '@/constants/auth'
import { useForgotPassword } from '@/hooks/business/auth/useForgotPassword'
import {
    DocumentFormSchema,
    EmailFormSchema,
    type IDocumentForm,
    type IEmailForm,
} from '@/schemas/auth/authFormSchemas'
import Button from '~/components/Form/Button'
import Input from '~/components/Form/Input'
import GSwal from '~/components/GSwal'

type ISearchBy = 'email' | 'docNumber'
type IForgotPasswordForm = Partial<IEmailForm & IDocumentForm>

const ForgotPassword: React.FC = () => {
    const [radioValue, setRadioValue] = useState<ISearchBy>('email')

    const schema = useMemo(() => (radioValue === 'email' ? EmailFormSchema : DocumentFormSchema), [radioValue])

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<IForgotPasswordForm>({
        // Um formulário com dois schemas: o do e-mail ou o do documento, conforme a busca escolhida.
        resolver: zodResolver(schema as typeof EmailFormSchema) as never,
    })

    const router = useRouter()
    const { requestByEmail, requestByDocument } = useForgotPassword()

    const onSubmit = async (data: IForgotPasswordForm) => {
        try {
            const message =
                radioValue === 'email'
                    ? await requestByEmail(data.email ?? '')
                    : await requestByDocument(data.MainDocument ?? '')

            await GSwal.fire({
                icon: 'success',
                title: 'Sucesso',
                html: `
          <p>${message}</p>
          <p class="mt-2 text-error text-sm">${PASSWORD_LINK_EXPIRATION_NOTICE}</p>
        `,
            })

            router.push('/login')
        } catch {
            // O service não avisa (a tela decide): falha de rede, 500 ou documento recusado.
            await GSwal.fire({
                icon: 'warning',
                title: 'Oops',
                html: `
          <p>Algo deu errado, tente novamente mais tarde.</p>
        `,
            })
        }
    }

    return (
        <div className="container mt-10 mx-auto pt-8 pb-16 px-14 max-md:mb-[150px] min-h-[350px] items-start justify-between">
            <div className="w-full">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <h2 className="text-primary font-sora text-base mb-4">
                        Para identificarmos sua conta, precisamos que você selecione a maneira como devemos procurar:
                    </h2>

                    <div className="flex gap-4 max-lg:justify-center mb-6">
                        <label className="flex gap-2 flex-row-reverse cursor-pointer">
                            E-mail
                            <input
                                type="radio"
                                name="contact"
                                id="email"
                                onChange={() => setRadioValue('email')}
                                checked={radioValue === 'email'}
                                value="email"
                            />
                        </label>
                        <label className="flex gap-2 flex-row-reverse cursor-pointer">
                            CPF ou CNPJ
                            <input
                                type="radio"
                                name="contact"
                                id="docNumber"
                                onChange={() => setRadioValue('docNumber')}
                                checked={radioValue === 'docNumber'}
                                value="docNumber"
                            />
                        </label>
                    </div>

                    <div className={`flex flex-row gap-2 max-md:flex-col max-md:items-start`}>
                        {radioValue === 'email' ? (
                            <Input
                                register={register('email')}
                                error={errors.email?.message}
                                placeholder="exemplo@email.com"
                                labelText="E-mail"
                                className="w-full rounded-sm"
                                isRequired
                            />
                        ) : (
                            <DynamicMaskedInput
                                control={control}
                                name="MainDocument"
                                placeholder="CPF ou CNPJ"
                                labelText="Número do documento (CPF ou CNPJ)"
                                className="w-full rounded-sm"
                                isRequired
                            />
                        )}
                    </div>
                    <div className="mt-3 flex">
                        <Button type="submit" size="md" color="primary" className="rounded-full">
                            Enviar
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default ForgotPassword
